/**
 * ShapeRenderer Component
 *
 * Renders Shape geometry on the map using MapLibre GL JS Source + Layers + Turf.js.
 * Supports rectangle, circle, and triangle shapes with fill and border customization.
 *
 * Features:
 * - MapLibre Polygon rendering (2 layers: fill + border)
 * - Shape generation with Turf.js
 * - Fill toggle (None/Solid)
 * - Border style customization (dash arrays)
 * - Optional center marker
 * - Hover highlighting
 * - Performance optimized (useMemo)
 *
 * @module components/map/GeometryRenderer/ShapeRenderer
 */

import { memo, useMemo } from 'react';
import { Source, Layer } from '@vis.gl/react-maplibre';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useGeometryContextMenu } from '../../../../hooks/useGeometryContextMenu';
import { useArtifactContextMenuForGeometry } from '../../../../hooks/useArtifactContextMenuForGeometry';
import { useGeometryHover } from '../../../../hooks/useGeometryHover';
import { useGeometryInteraction } from '../../../../hooks/useGeometryInteraction';
import { useGeometrySelection } from '../../../../hooks/useGeometrySelection';
import { useShapeDrag } from '../../../../hooks/useShapeDrag';
import { useArtifactGeometryHoverPreview } from '@/hooks/useArtifactGeometryHoverPreview';
import { useGeoMarkupStore } from '../../../../store';
import { FillType, GeometryType, ShapeType } from '../../../../types/geometry';
import { MapContextMenu, createTaskMenuItems } from '../../MapContextMenu';
import { ShapeResizeHandles } from './ShapeResizeHandles';
import { DeleteConfirmationDialog } from '../../../ui/DeleteConfirmationDialog';
import { createShapeGeoJson } from './utils/shapeGeoJsonHelpers';
import { getBorderDashArray } from './utils/shapeStyleHelpers';
import { devError } from '../../../../utils/logger';
import {
  DEFAULT_SHAPE_FILL_COLOR,
  SHAPE_DEFAULT_BORDER_WIDTH,
  SHAPE_DEFAULT_BORDER_OPACITY,
  SHAPE_FILL_OPACITY_SOLID,
  SHAPE_FILL_OPACITY_SEMI_TRANSPARENT,
  SHAPE_FILL_OPACITY_HOVER_BOOST,
  SHAPE_BORDER_HOVER_WIDTH_BOOST,
  SHAPE_BORDER_SELECTION_WIDTH_BOOST,
  SHAPE_HOVER_GLOW_WIDTH,
  SHAPE_HOVER_GLOW_OPACITY,
  SHAPE_HOVER_GLOW_BLUR,
  SHAPE_SELECTION_GLOW_COLOR,
  SHAPE_SELECTION_GLOW_WIDTH,
  SHAPE_SELECTION_GLOW_OPACITY,
  SHAPE_SELECTION_GLOW_BLUR,
  BLUR_HOVERED,
  LINE_JOIN_STYLE,
  LINE_CAP_STYLE,
} from '../../../../constants';
import type { TaskGeometry } from '../../../../types';

/**
 * Props for ShapeRenderer component
 */
export interface ShapeRendererProps {
  /** The shape geometry to render */
  geometry: TaskGeometry;
  /** ID of the task this geometry belongs to */
  taskId: string;
  /** Whether this geometry is selected */
  isSelected: boolean;
  /** Current map zoom level */
  zoom: number;
}

/**
 * ShapeRenderer Component
 *
 * Renders a shape geometry on the MapLibre map with multi-geometry support.
 */
export const ShapeRenderer = memo(function ShapeRenderer({
  geometry,
  taskId,
  isSelected: _isSelected, // Available for future selected state rendering
  zoom: _zoom, // Available for future center marker scaling
}: ShapeRendererProps) {
  // Extract geometry properties
  const shapeFill = geometry.shapeFill || FillType.None;
  const shapeFillColor = geometry.shapeFillColor || DEFAULT_SHAPE_FILL_COLOR;
  const shapeBorderColor =
    geometry.shapeBorderColor || DEFAULT_SHAPE_FILL_COLOR; // Border fallback = Fill color
  const shapeBorderWidth =
    geometry.shapeBorderWidth || SHAPE_DEFAULT_BORDER_WIDTH;
  const calculatedCenter = geometry.calculatedCenter;

  // Check if shape has enough points (BEFORE hooks to comply with Rules of Hooks)
  const shapePoints = geometry.shapePoints || [];

  // Generate GeoJSON from geometry (memoized, with early guard for empty shapes)
  const geoJsonShape = useMemo(() => {
    // Calculate required points based on shape type
    const requiredPoints = (() => {
      switch (geometry.shapeType) {
        case ShapeType.Rectangle:
          return 4;
        case ShapeType.Circle:
          return 2;
        case ShapeType.Triangle:
          return 3;
        case ShapeType.Polygon:
          return 3;
        default:
          return 0;
      }
    })();

    // Guard: Empty shape (expected during tool activation)
    // Return null silently - this is normal behavior for Shape tool (like Line tool)
    if (shapePoints.length < requiredPoints) {
      return null;
    }

    // Try to create GeoJSON (only for complete shapes)
    try {
      return createShapeGeoJson({ geometry });
    } catch (error) {
      devError(
        `Failed to create shape GeoJSON for geometry ${geometry.id}:`,
        error
      );
      return null;
    }
  }, [geometry, shapePoints.length]);

  // Get border dash array (memoized, before hooks for early data prep)
  const borderDashArray = useMemo(
    () => getBorderDashArray(geometry.shapeBorderStyle),
    [geometry.shapeBorderStyle]
  );

  // Interactive layer ID for PPM
  const interactiveLayerId = `shape-hitbox-${geometry.id}`;

  // PPM (right-click) context menu for shape (GEOMETRY menu - during editing)
  useGeometryContextMenu({
    geometryId: geometry.id,
    geometryType: GeometryType.Shape,
    interactiveLayerId: interactiveLayerId,
    isPreview: false,
  });

  // Task-level context menu for free roam mode (TASK menu - when form closed)
  const { t } = useTranslation();
  const task = useGeoMarkupStore((s) =>
    s.tasks.items.find((t) => t.id === taskId)
  );

  // Context menu state from Zustand (unified state)
  const contextMenu = useGeoMarkupStore((s) => s.ui.contextMenu);
  const hideContextMenu = useGeoMarkupStore((s) => s.ui.hideContextMenu);
  const isTaskDetailsOpen = useGeoMarkupStore((s) => s.tasks.detailsIsOpen);
  const selectedTaskId = useGeoMarkupStore((s) => s.tasks.selectedId);

  // Check if task context menu should be visible for THIS specific geometry
  const showTaskContextMenu =
    contextMenu.isVisible &&
    contextMenu.type === 'task' &&
    contextMenu.taskData?.taskId === taskId &&
    contextMenu.taskData?.geometryId === geometry.id;

  const {
    showDeleteDialog,
    handleShowDetails,
    handleEditArtifact: handleEditTask,
    handleDeleteClick,
    confirmDelete,
    cancelDelete,
    handleCreateHere,
  } = useArtifactContextMenuForGeometry({
    artifactKind: 'task',
    interactiveLayerId,
    artifactId: taskId,
    geometryId: geometry.id,
    coordinates: calculatedCenter || { lat: 0, lng: 0 },
  });

  // Context-aware task menu items (only "Create task here" when previewing this task)
  const finalTaskMenuItems = useMemo(() => {
    if (!task) return [];

    const isTaskBeingViewed = isTaskDetailsOpen && selectedTaskId === taskId;

    if (isTaskBeingViewed) {
      return [
        {
          icon: <Plus size={14} />,
          label: t('contextMenu.createTaskHere'),
          onClick: handleCreateHere,
        },
      ];
    }

    return createTaskMenuItems(
      handleShowDetails,
      handleEditTask,
      handleDeleteClick,
      handleCreateHere,
      t
    );
  }, [
    task,
    isTaskDetailsOpen,
    selectedTaskId,
    taskId,
    handleShowDetails,
    handleEditTask,
    handleDeleteClick,
    handleCreateHere,
    t,
  ]);

  // Unified click/double-click interaction (replaces useGeometryClick + useGeometryDoubleClick)
  // - Single click: Open task preview (delayed 250ms to allow double-click detection)
  // - Double click: Activate edit mode (loads geometries, opens form)
  // - Mobile: Long press activates edit mode
  useGeometryInteraction({
    geometry,
    taskId,
    interactiveLayerId,
    isPreview: false,
  });

  // Selection mode - single click to select (does NOT activate drawing tool)
  useGeometrySelection({
    geometryId: geometry.id,
    interactiveLayerId,
  });

  // Drag functionality for entire shape (uses QueryRenderedFeatures strategy)
  const { isDraggable: isShapeDraggable } = useShapeDrag(geometry.id, taskId);

  // Hover highlighting
  const { isHovered, hoverPoint } = useGeometryHover({
    geometryId: geometry.id,
    interactiveLayerId,
    isDraggable: isShapeDraggable, // from useShapeDrag - entire shape
    isPreview: false,
  });

  useArtifactGeometryHoverPreview({
    artifactKind: 'task',
    artifactId: taskId,
    isHovered,
    coordinates: hoverPoint ?? calculatedCenter,
  });

  // Check if this geometry is selected in Selection mode (from Zustand)
  const selectedGeometryId = useGeoMarkupStore((s) => s.selectedGeometryId);
  const activeTool = useGeoMarkupStore((s) => s.activeTool);
  const isSelectedInSelectionMode =
    selectedGeometryId === geometry.id && activeTool === GeometryType.Selection;

  // Early return AFTER all hooks (Rules of Hooks compliance)
  // Guard: Check if we have valid GeoJSON (includes empty shape check from useMemo)
  if (!geoJsonShape) {
    return null;
  }

  const hasFill = shapeFill !== FillType.None;

  // Determine base opacity based on fill type
  const baseFillOpacity =
    shapeFill === FillType.Solid
      ? SHAPE_FILL_OPACITY_SOLID
      : SHAPE_FILL_OPACITY_SEMI_TRANSPARENT;

  // Fill layer paint properties (type-safe with const assertion)
  // Visual feedback: selected state in Selection mode (similar to hover but persistent)
  const fillPaint = {
    'fill-color': shapeFillColor,
    'fill-opacity':
      isHovered || isSelectedInSelectionMode
        ? Math.min(baseFillOpacity + SHAPE_FILL_OPACITY_HOVER_BOOST, 1.0)
        : baseFillOpacity,
  } as const;

  // Calculate border width based on interaction state
  // Priority: Selection > Hover (selection has thicker border)
  const borderWidth = (() => {
    if (isSelectedInSelectionMode) {
      return shapeBorderWidth + SHAPE_BORDER_SELECTION_WIDTH_BOOST; // +3px
    }
    if (isHovered) {
      return shapeBorderWidth + SHAPE_BORDER_HOVER_WIDTH_BOOST; // +1.5px
    }
    return shapeBorderWidth; // Base width
  })();

  // Calculate glow layer offsets to position them OUTSIDE the border
  // IMPORTANT: For polygons, NEGATIVE offset = outward, POSITIVE = inward (MapLibre spec)
  // Formula: -(borderWidth/2 + glowExtraWidth/2)
  // This ensures glow is fully visible outside the shape boundary
  const hoverGlowOffset = -(shapeBorderWidth / 2 + SHAPE_HOVER_GLOW_WIDTH / 2);
  const selectionGlowOffset = -(
    shapeBorderWidth / 2 +
    SHAPE_SELECTION_GLOW_WIDTH / 2
  );

  // Border layer paint properties (type-safe with const assertion)
  // Visual feedback: hover and selection states with different border widths
  // line-offset removed to prevent gaps at acute angles (line-join: 'round' handles smooth corners)
  const borderPaint = {
    'line-color': shapeBorderColor,
    'line-width': borderWidth,
    'line-dasharray': borderDashArray,
    'line-opacity': SHAPE_DEFAULT_BORDER_OPACITY,
    ...((isHovered || isSelectedInSelectionMode) && {
      'line-blur': BLUR_HOVERED,
    }),
  } as const;

  return (
    <>
      {/* MapLibre Source + Layers for Shape */}
      <Source
        id={`shape-source-${geometry.id}`}
        type="geojson"
        data={geoJsonShape}
      >
        {/* Fill Layer (optional, rendered FIRST on bottom so glow is visible above it) */}
        {hasFill && (
          <Layer
            id={`shape-fill-${geometry.id}`}
            type="fill"
            paint={fillPaint}
          />
        )}

        {/* Border Layer (sharp, rendered SECOND above fill) */}
        <Layer
          id={`shape-border-${geometry.id}`}
          type="line"
          paint={borderPaint}
          layout={{
            'line-cap': LINE_CAP_STYLE,
            'line-join': LINE_JOIN_STYLE,
          }}
        />

        {/* Hover Glow Layer - subtle effect in shape's own color (rendered THIRD, above border - always visible!) */}
        {isHovered && (
          <Layer
            id={`shape-hover-glow-${geometry.id}`}
            type="line"
            paint={{
              'line-color': shapeBorderColor, // Same color as shape border
              'line-width': shapeBorderWidth + SHAPE_HOVER_GLOW_WIDTH,
              'line-offset': hoverGlowOffset, // Negative = outward for polygons
              'line-opacity': SHAPE_HOVER_GLOW_OPACITY,
              'line-blur': SHAPE_HOVER_GLOW_BLUR,
            }}
            layout={{
              'line-cap': LINE_CAP_STYLE,
              'line-join': LINE_JOIN_STYLE,
            }}
          />
        )}

        {/* Selection Glow Layer - strong blue accent (rendered FOURTH, above border - always visible!) */}
        {isSelectedInSelectionMode && (
          <Layer
            id={`shape-selection-glow-${geometry.id}`}
            type="line"
            paint={{
              'line-color': SHAPE_SELECTION_GLOW_COLOR, // Blue accent
              'line-width': shapeBorderWidth + SHAPE_SELECTION_GLOW_WIDTH,
              'line-offset': selectionGlowOffset, // Negative = outward for polygons
              'line-opacity': SHAPE_SELECTION_GLOW_OPACITY,
              'line-blur': SHAPE_SELECTION_GLOW_BLUR,
            }}
            layout={{
              'line-cap': LINE_CAP_STYLE,
              'line-join': LINE_JOIN_STYLE,
            }}
          />
        )}

        {/* Interactive hitbox layer (transparent, for PPM and D&D, rendered LAST on top) */}
        <Layer
          id={interactiveLayerId}
          type="fill"
          paint={{
            'fill-color': 'transparent',
            'fill-opacity': 0,
          }}
        />
      </Source>

      {/* Resize Handles (corners/vertices for shape resizing) */}
      <ShapeResizeHandles geometry={geometry} isDraggable={isShapeDraggable} />

      {/* Task Context Menu (free roam mode - from unified Zustand state) */}
      {task && showTaskContextMenu && (
        <MapContextMenu
          isVisible={showTaskContextMenu}
          position={contextMenu.position}
          items={finalTaskMenuItems}
          onClose={hideContextMenu}
        />
      )}

      {/* Delete Confirmation Dialog (free roam mode) */}
      {task && showDeleteDialog && (
        <DeleteConfirmationDialog
          isOpen={showDeleteDialog}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          itemName={task.title}
          itemType="task"
        />
      )}
    </>
  );
});
